import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";
import { useAppDispatch } from "@/app/hooks";
import { setScrollPosition } from "@/modules/hub/features/ResourceLibrary/slices/resourceLibrarySlice";
import { FavoriteButton } from "@/modules/hub/features/ResourceLibrary/components/FavoriteButton/FavoriteButton";
import { toggleResourceFavorite } from "@/api/favoriteResource/favoriteResourceApi";
import { trackResourceCardClick } from "@/modules/hub/features/ResourceLibrary/analytics/resourceLibraryAnalytics";
import { trackPodcastCardClick } from "@/modules/hub/features/Podcasts/analytics/podcastsAnalytics";
import { trackNewsCardClick } from "@/modules/hub/features/News/analytics/newsAnalytics";

interface MinimalResourceCardProps {
  resource: GetResourceSearchResultsItem;
  onToggleFavorite?: (uuid: string, isFavorited: boolean) => void;
}

export function MinimalResourceCard({
  resource,
  onToggleFavorite,
}: MinimalResourceCardProps) {
  const dispatch = useAppDispatch();
  const [isFavorited, setIsFavorited] = useState(resource.isFavorited ?? false);

  const contentType = (resource.resourceType || "document").toLowerCase();

  const handleCardClick = () => {
    const scrollPosition =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;

    dispatch(setScrollPosition(scrollPosition));

    if (resource.resourceType === "Podcast") {
      trackPodcastCardClick({
        resourceType: resource.resourceType,
        resourceUuid: resource.uuid,
      });
    } else if (resource.resourceType === "News") {
      trackNewsCardClick({
        resourceType: resource.resourceType,
        resourceUuid: resource.uuid,
      });
    } else {
      trackResourceCardClick({
        resourceType: resource.resourceType || "Unknown",
        resourceUuid: resource.uuid,
      });
    }
  };

  const handleToggleFavorite = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const previousState = isFavorited;
    setIsFavorited(!isFavorited);

    try {
      await toggleResourceFavorite({ resourceUuid: resource.uuid });
      if (onToggleFavorite) {
        onToggleFavorite(resource.uuid, !previousState);
      }
    } catch (error) {
      setIsFavorited(previousState);
      throw error;
    }
  };

  const isPartnerPage =
    resource.resourceType === "Partner Page" && resource.contentUrl;

  return (
    <div className="group relative">
      {/* Favorite Button - appears on hover */}
      <div className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        <FavoriteButton
          isFavorited={isFavorited}
          onToggle={handleToggleFavorite}
        />
      </div>

      {isPartnerPage ? (
        <a
          className="block"
          href={resource.contentUrl || ""}
          rel="noopener noreferrer"
          target="_blank"
        >
          <div className="relative overflow-hidden rounded-lg mb-3">
            {resource.thumbnailUrl ? (
              <img
                alt={resource.title}
                className="block w-full h-auto transition-transform duration-300 group-hover:scale-105"
                src={resource.thumbnailUrl}
              />
            ) : resource.resourceType === "File" ? (
              <div className="w-full aspect-video flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
                <span
                  className="inline-block relative z-10"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                  style={{
                    filter: "brightness(0) invert(1)",
                    opacity: 0.9,
                  }}
                />
              </div>
            ) : (
              <div className="w-full aspect-video flex items-center justify-center bg-gray-100 dark:bg-slate-700">
                <span
                  className="inline-block"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                />
              </div>
            )}
          </div>

          <div className="space-y-1">
            <p className="text-xs text-gray-400 dark:text-gray-500 font-light">
              {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
            </p>
            <h3 className="text-sm font-normal text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors line-clamp-2">
              {resource.title}
            </h3>
          </div>
        </a>
      ) : (
        <Link
          className="block"
          onClick={handleCardClick}
          to={`/hub/resources/${resource.slug}`}
        >
          <div className="relative overflow-hidden rounded-lg mb-3">
            {resource.thumbnailUrl ? (
              <img
                alt={resource.title}
                className="block w-full h-auto transition-transform duration-300 group-hover:scale-105"
                src={resource.thumbnailUrl}
              />
            ) : resource.resourceType === "File" ? (
              <div className="w-full aspect-video flex items-center justify-center bg-gradient-to-b from-red-300 to-red-400 dark:from-red-400 dark:to-red-600 relative overflow-hidden">
                <span
                  className="inline-block relative z-10"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                  style={{
                    filter: "brightness(0) invert(1)",
                    opacity: 0.9,
                  }}
                />
              </div>
            ) : (
              <div className="w-full aspect-video flex items-center justify-center bg-gray-100 dark:bg-slate-700">
                <span
                  className="inline-block"
                  dangerouslySetInnerHTML={{
                    __html: resource.primaryIcon ?? "",
                  }}
                />
              </div>
            )}
          </div>

          <div className="space-y-1">
            <p className="text-xs text-gray-400 dark:text-gray-500 font-light">
              {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
            </p>
            <h3 className="text-sm font-normal text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors line-clamp-2">
              {resource.title}
            </h3>
          </div>
        </Link>
      )}
    </div>
  );
}
